import { Transition } from '@headlessui/react';
import { Fragment, ReactNode, useCallback, useState } from 'react';
import { ChevronDownWhiteIcon, GridDotsWhiteIcon, GridWhiteIcon, MenuWhiteIcon } from '../icons';
import { Avatar } from './Avatar';
import { DropdownMenu, DropdownMenuItem } from './DropdownMenu';
import { PopoverMenu } from './PopoverMenu';
import { MenuBlock } from './Sidebar/MenuBlock';
import { AppSelectorInfo, SidebarMenuItem } from './Sidebar/types';

interface TopBarProps {
  user?: any;
  role?: string;
  entity?: {
    type?: string;
  };
  label?: ReactNode;
  header?: 'menu' | 'search';
  profileMenuItems?: DropdownMenuItem[];
  headerMenuItems?: DropdownMenuItem[];
  headerMenuSelectedValue?: string;
  profileHeaderMenu?: JSX.Element;
  compliance?: boolean;
  avatarUrl?: string;
  menuItems: SidebarMenuItem[];
  appSelectorInfo?: AppSelectorInfo;
}

export const TopBar = ({
  user,
  entity,
  header,
  avatarUrl,
  menuItems,
  headerMenuItems,
  profileMenuItems,
  profileHeaderMenu,
  headerMenuSelectedValue,
  appSelectorInfo,
  label = 'Compli One',
  compliance = false,
  role,
}: TopBarProps) => {
  const [isOpened, setIsOpened] = useState<boolean>(false);

  const handleToggleOpen = useCallback(() => {
    setIsOpened(open => !open);
  }, []);

  return (
    <header className="bg-[#171717] h-[56px] w-full flex text-white sm:hidden">
      <div className="p-4 text-2xl font-serif leading-6 font-semibold flex-1">
        <span>{label}</span>
      </div>
      {appSelectorInfo && (
        <PopoverMenu
          classes={{ container: '!w-auto z-10', panel: 'w-[220px] !left-[-40px] shadow-xl' }}
          button={<img src={GridDotsWhiteIcon} alt="app selector" className="p-4" />}
          items={appSelectorInfo.items}
        />
      )}
      {header && (
        <div className="flex justify-end w-[100px]">
          {header === 'menu' && (
            <DropdownMenu
              items={headerMenuItems || []}
              placement="bottom-end"
              className="w-[280px] shadow-lg border"
              selectedValue={headerMenuSelectedValue}
              button={
                <button className="flex items-center gap-2 p-4">
                  <img className="select-none" src={GridWhiteIcon} alt="Selection" />
                  <img className="select-none" src={ChevronDownWhiteIcon} alt="Selection" />
                </button>
              }
            />
          )}
          {header && <div className="my-3 border-r border-[#FFFFFF1F]" />}
        </div>
      )}
      <div className="p-4 cursor-pointer" onClick={handleToggleOpen}>
        <img src={MenuWhiteIcon} alt="Menu" className="select-none" />
      </div>
      {isOpened && (
        <Transition
          show={isOpened}
          as={Fragment}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <div className="fixed h-svh top-[56px] pb-[56px] flex flex-col justify-between w-screen bg-[#171717] z-10">
            <MenuBlock menuItems={menuItems} isOpened={isOpened} setIsOpened={setIsOpened} />

            <PopoverMenu
              items={profileMenuItems || []}
              headerMenu={profileHeaderMenu}
              placement="top-start"
              button={
                <div className="w-full flex items-center px-6 pb-6 space-x-3">
                  <Avatar src={avatarUrl} firstName={user?.first_name} lastName={user?.last_name} />
                  {isOpened && (
                    <>
                      <div className="flex flex-col items-start capitalize">
                        {compliance ? (
                          <div className="flex flex-col items-start space-y-1">
                            <p className="text-[14px] leading-[22px]">
                              {user?.first_name} {user?.last_name}
                            </p>
                            <div className="w-full flex items-center space-x-1">
                              <p className="text-[11px] leading-[13px] opacity-64">
                                {user?.business.name}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-start">
                            <p className="text-[14px] leading-[22px]">
                              {user?.first_name} {user?.last_name}
                            </p>
                            <p className="text-[12px] leading-[20px] opacity-64">
                              {role || entity?.type}
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              }
            />
          </div>
        </Transition>
      )}
    </header>
  );
};
